import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  SaihArtifactData,
  SaihContext,
  SaihMessage,
  SaihProgressData,
  SaihProposal,
  SaihVocabulary
} from '../models/saih.models';
import {
  AcceptProposal,
  ClearSession,
  LoadVocabularyStarted,
  ReceiveSseEvent,
  SeedContext,
  SelectModel,
  SendMessage,
  SessionCreationFailed,
  SessionCreationStarted,
  StartSession,
  VocabularyLoadFailed,
  VocabularyLoaded
} from './saih.actions';

export class SaihStateModel {
  sessionId: string | null = null;
  messages: SaihMessage[] = [];
  currentTaskId: string | null = null;
  proposal: SaihProposal | null = null;
  context: SaihContext | null = null;
  selectedModel: string | null = null;
  loading = false;
  streaming = false;
  creatingSession = false;
  progress: SaihProgressData | null = null;
  /** Artefactos generados por los turnos de ESTA sesion (INETUM2025-1896).
   * Se acumulan: lo que produjo un turno anterior sigue descargable mientras
   * no expire, sin tener que volver a pedirlo. */
  turnArtifacts: SaihArtifactData[] = [];
  /** Vocabulario canónico de perfil/fase (RF-106, sede única D-03). Se carga
   * una vez por sesión desde `lifecycle-tab`; `null` mientras no ha llegado
   * ninguna respuesta (INETUM2025-1662). */
  vocabulary: SaihVocabulary | null = null;
  vocabularyLoading = false;
  vocabularyError: string | null = null;
}

export const SaihStateDefaults: SaihStateModel = {
  sessionId: null,
  messages: [],
  currentTaskId: null,
  proposal: null,
  context: null,
  selectedModel: null,
  loading: false,
  streaming: false,
  creatingSession: false,
  progress: null,
  turnArtifacts: [],
  vocabulary: null,
  vocabularyLoading: false,
  vocabularyError: null
};

@State<SaihStateModel>({
  name: 'saih',
  defaults: SaihStateDefaults
})
@Injectable({ providedIn: 'root' })
export class SaihState {

  @Selector()
  static getSessionId(state: SaihStateModel) {
    return state ? state.sessionId : null;
  }

  @Selector()
  static getMessages(state: SaihStateModel) {
    return state ? state.messages : [];
  }

  @Selector()
  static getTurnArtifacts(state: SaihStateModel) {
    return state ? state.turnArtifacts : [];
  }

  @Selector()
  static getProposal(state: SaihStateModel) {
    return state ? state.proposal : null;
  }

  @Selector()
  static getContext(state: SaihStateModel) {
    return state ? state.context : null;
  }

  @Selector()
  static getSelectedModel(state: SaihStateModel) {
    return state ? state.selectedModel : null;
  }

  @Selector()
  static isStreaming(state: SaihStateModel) {
    return state ? state.streaming : false;
  }

  @Selector()
  static isCreatingSession(state: SaihStateModel) {
    return state ? state.creatingSession : false;
  }

  @Selector()
  static getProgress(state: SaihStateModel) {
    return state ? state.progress : null;
  }

  @Selector()
  static getVocabulary(state: SaihStateModel) {
    return state ? state.vocabulary : null;
  }

  @Selector()
  static isVocabularyLoading(state: SaihStateModel) {
    return state ? state.vocabularyLoading : false;
  }

  @Selector()
  static getVocabularyError(state: SaihStateModel) {
    return state ? state.vocabularyError : null;
  }

  @Action(StartSession)
  startSession(ctx: StateContext<SaihStateModel>, action: StartSession) {
    ctx.patchState({ sessionId: action.sessionId, creatingSession: false });
  }

  @Action(SessionCreationStarted)
  sessionCreationStarted(ctx: StateContext<SaihStateModel>) {
    ctx.patchState({ creatingSession: true });
  }

  @Action(SessionCreationFailed)
  sessionCreationFailed(ctx: StateContext<SaihStateModel>, action: SessionCreationFailed) {
    const state = ctx.getState();
    ctx.patchState({
      creatingSession: false,
      messages: [...state.messages, { role: 'sys', content: this.errorText(action.message) }]
    });
  }

  @Action(SendMessage)
  sendMessage(ctx: StateContext<SaihStateModel>, action: SendMessage) {
    const state = ctx.getState();
    ctx.patchState({
      messages: [...state.messages, { role: 'me', content: action.message }],
      streaming: true
    });
  }

  @Action(ReceiveSseEvent)
  receiveSseEvent(ctx: StateContext<SaihStateModel>, action: ReceiveSseEvent) {
    const state = ctx.getState();
    const { type, data } = action.event;

    switch (type) {
      case 'progress':
        ctx.patchState({ progress: data as SaihProgressData });
        break;
      case 'answer': {
        const messages = [...state.messages];
        const last = messages[messages.length - 1];
        const chunk = this.answerText(data);
        if (last && last.role === 'bot' && state.streaming) {
          messages[messages.length - 1] = { role: 'bot', content: last.content + chunk };
        } else {
          messages.push({ role: 'bot', content: chunk });
        }
        ctx.patchState({ messages });
        break;
      }
      case 'proposal':
        ctx.patchState({ proposal: data as SaihProposal });
        break;
      case 'artifact':
        ctx.patchState({ turnArtifacts: [...state.turnArtifacts, data as SaihArtifactData] });
        break;
      case 'notice': {
        // INETUM2025-2029: el aviso NO cierra el turno (el modelo puede seguir contestando), así que no se
        // tocan `streaming` ni `progress`. Un código desconocido se ignora y el mismo aviso no se repite.
        const aviso = this.noticeMessage(data);
        const repetido = aviso && state.messages.some(
          m => m.role === 'sys' && m.content === aviso.content && m.action?.route === aviso.action?.route
        );
        if (aviso && !repetido) {
          ctx.patchState({ messages: [...state.messages, aviso] });
        }
        break;
      }
      case 'error':
        ctx.patchState({
          messages: [...state.messages, this.errorMessage(data)],
          streaming: false,
          progress: null
        });
        break;
      case 'done':
        ctx.patchState({ streaming: false, progress: null });
        break;
      case 'metadata':
      default:
        break;
    }
  }

  @Action(AcceptProposal)
  acceptProposal(ctx: StateContext<SaihStateModel>, action: AcceptProposal) {
    ctx.patchState({
      proposal: null,
      currentTaskId: action.taskId ?? ctx.getState().currentTaskId
    });
  }

  @Action(SeedContext)
  seedContext(ctx: StateContext<SaihStateModel>, action: SeedContext) {
    ctx.patchState({ context: action.context });
  }

  @Action(SelectModel)
  selectModel(ctx: StateContext<SaihStateModel>, action: SelectModel) {
    ctx.patchState({ selectedModel: action.model });
  }

  @Action(ClearSession)
  clearSession(ctx: StateContext<SaihStateModel>) {
    ctx.setState({ ...SaihStateDefaults });
  }

  @Action(LoadVocabularyStarted)
  loadVocabularyStarted(ctx: StateContext<SaihStateModel>) {
    ctx.patchState({ vocabularyLoading: true, vocabularyError: null });
  }

  @Action(VocabularyLoaded)
  vocabularyLoaded(ctx: StateContext<SaihStateModel>, action: VocabularyLoaded) {
    ctx.patchState({
      vocabulary: action.vocabulary,
      vocabularyLoading: false,
      vocabularyError: null
    });
  }

  @Action(VocabularyLoadFailed)
  vocabularyLoadFailed(ctx: StateContext<SaihStateModel>, action: VocabularyLoadFailed) {
    ctx.patchState({
      vocabularyLoading: false,
      vocabularyError: action.message || 'SAIH.ERROR.GENERICO'
    });
  }

  /** Extrae el fragmento de texto del payload del evento `answer`: el backend
   * envía un objeto `{ text }`; se admite también un string plano. */
  private answerText(data: any): string {
    if (typeof data === 'string') {
      return data;
    }
    return data && typeof data.text === 'string' ? data.text : '';
  }

  /**
   * Avisos que el usuario puede resolver por sí mismo (INETUM2025-1914).
   *
   * El texto sale de claves i18n, **nunca del `message` del backend**: ese mensaje es técnico y el
   * criterio de la tarea dice que el aviso no debe mostrar el detalle del error, sólo lo accionable
   * y el enlace. La ruta la decide el front, porque sabe dónde están sus propias pantallas; desde
   * INETUM2025-2029 el `deepLink` del backend ya no se sigue (ver `avisoMessage`).
   */
  private static readonly AVISOS_ACCIONABLES: Record<string, { textKey: string; labelKey: string; route: string }> = {
    NO_TOKEN: SaihState.avisoP01('NO_TOKEN'),
    // INETUM2025-2029: la misma lista cerrada que ms-siah (`notice_service.ACTIONABLE_CODES`) y el conector
    // (`ACTIONABLE_NOTICES`); saih.state.notice-2029.spec.ts falla si una cambia sola.
    JIRA_UNAUTHORIZED: SaihState.avisoP01('JIRA_UNAUTHORIZED'),
    CONFLUENCE_UNAUTHORIZED: SaihState.avisoP01('CONFLUENCE_UNAUTHORIZED'),
    AZURE_UNAUTHORIZED: SaihState.avisoP01('AZURE_UNAUTHORIZED'),
    AMBIGUOUS_ORGANIZATION: SaihState.avisoP01('AMBIGUOUS_ORGANIZATION'),
    NO_TENANT_CONFIG: SaihState.avisoP02('NO_TENANT_CONFIG'),
    CONFLUENCE_CLOUD_NOT_SUPPORTED: SaihState.avisoP02('CONFLUENCE_CLOUD_NOT_SUPPORTED')
  };

  private static avisoP01(codigo: string) {
    return { textKey: `SAIH.AVISO.${codigo}`, labelKey: 'SAIH.AVISO.LINK_P01', route: '/jira-config' };
  }

  private static avisoP02(codigo: string) {
    return { textKey: `SAIH.AVISO.${codigo}`, labelKey: 'SAIH.AVISO.LINK_P02', route: '/gdb/admin/tracker-connections' };
  }

  /** Aviso del evento `notice` (INETUM2025-2029), o `null` si el código no es accionable. */
  private noticeMessage(data: any): SaihMessage | null {
    const codigo = data && typeof data === 'object' ? data.code : undefined;
    const aviso = typeof codigo === 'string' ? SaihState.AVISOS_ACCIONABLES[codigo] : undefined;
    return aviso ? this.avisoMessage(aviso) : null;
  }

  /**
   * INETUM2025-2029: la ruta del botón la decide SIEMPRE el front, por código. El `deepLink` del backend
   * ya no se sigue: ms-siah lo canoniza, pero un enlace arbitrario pintado en el chat sería un
   * open-redirect, y la única ruta válida para un código es justo la que el front ya conoce.
   */
  private avisoMessage(aviso: { textKey: string; labelKey: string; route: string }): SaihMessage {
    return {
      role: 'sys',
      content: aviso.textKey,
      action: { labelKey: aviso.labelKey, route: aviso.route }
    };
  }

  private errorMessage(data: any): SaihMessage {
    const codigo = data && typeof data === 'object' ? data.code : undefined;
    const aviso = typeof codigo === 'string' ? SaihState.AVISOS_ACCIONABLES[codigo] : undefined;

    if (!aviso) {
      // Sin código conocido, exactamente el comportamiento de siempre.
      return { role: 'sys', content: this.errorText(data) };
    }
    return this.avisoMessage(aviso);
  }

  private errorText(data: any): string {
    // INETUM2025-1915: se devuelve una CLAVE, no texto. El estado NGXS no inyecta
    // TranslateService — se mantiene puro y testable sin i18n, igual que en INETUM2025-1914, y es
    // la plantilla quien traduce.
    if (!data) {
      return 'SAIH.ERROR.GENERICO';
    }
    return typeof data === 'string' ? data : (data.message || 'SAIH.ERROR.GENERICO');
  }
}
